import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleNotch } from "@phosphor-icons/react";
import { apiGet, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { formatCurrency, formatDateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { computeWorkContractWithholding, type WithholdingRates } from "@/lib/money/work-contract";

/** Kod odobrenog zahtjeva iznos je stvarna isplata; tooltip pokazuje i traženo i bruto. */
function requestAmountTitle(item: AdminPayoutRequest): string | undefined {
  if (item.paidAmount == null) return undefined;
  const parts = [`Traženo ${formatCurrency(item.amount)}`];
  if (item.paidWithheld && item.paidWithheld > 0 && item.paidGrossAmount != null) {
    parts.push(`bruto ${formatCurrency(item.paidGrossAmount)}`, `obustave ${formatCurrency(item.paidWithheld)}`);
  }
  parts.push(`isplaćeno ${formatCurrency(item.paidAmount)}`);
  return parts.join(", ");
}

/**
 * Tekst potvrde odobravanja. Kod ugovora o djelu obustave računa ista funkcija
 * kao server, sa stopama koje je server poslao uz listu.
 */
function approvePreviewText(item: AdminPayoutRequest, rates: WithholdingRates | null): string {
  const items = `za ${item.sessionCount} stavki (sesije i grupne terapije).`;
  if (item.therapistPayoutMethod === "work_contract" && rates) {
    const w = computeWorkContractWithholding(item.amount, rates);
    return `Kreirat će se isplata ${items} Bruto ${formatCurrency(w.gross)}, obustave ${formatCurrency(w.totalWithheld)}, na račun ${formatCurrency(w.net)}.`;
  }
  return `Kreirat će se isplata od ${formatCurrency(item.amount)} ${items}`;
}

interface AdminPayoutRequest {
  id: string;
  therapistName: string;
  amount: number;
  sessionCount: number;
  note: string | null;
  requestedAt: string;
  resolvedAt: string | null;
  resolution: "approved" | "rejected" | "withdrawn" | null;
  adminNote: string | null;
  payoutId: string | null;
  /** Stvarna isplata za odobren zahtjev; `amount` je samo snimak zahtjeva. */
  paidAmount: number | null;
  paidGrossAmount: number | null;
  paidWithheld: number | null;
  therapistPayoutMethod?: "invoice" | "work_contract";
}

interface AdminPayoutRequestsResponse {
  items: AdminPayoutRequest[];
  withholdingRates?: WithholdingRates;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface ApproveResult {
  payoutId: string;
  /** Stvarno isplaćeno na račun, neto nakon obustava. */
  amount: number;
  /** Obračunska osnovica prije obustava; ovo se poredi sa zatraženim iznosom. */
  grossAmount: number;
  /** Porez i doprinosi obustavljeni kod ugovora o djelu, 0 kod fakture. */
  totalWithheld: number;
  requestedAmount: number;
  sessionCount: number;
}

/**
 * Poruka nakon odobrenog zahtjeva.
 *
 * Zatraženi iznos (`requestedAmount`) je zbir `therapistEarnings` po sesijama, dakle
 * BRUTO osnovica. Poredi se isključivo sa `grossAmount`, nikad sa `amount`: `amount` je
 * neto nakon obustava, pa bi poređenje s njim kod svakog ugovora o djelu prijavilo
 * razliku i pripisalo je "već isplaćenim sesijama", što nije istina. Razlika između
 * bruto i neto su obustave i one se prikazuju zasebno.
 */
export function approveToastMessage(result: {
  amount: number;
  grossAmount: number;
  totalWithheld: number;
  requestedAmount: number;
}): string {
  const head =
    result.totalWithheld > 0
      ? `Isplata kreirana: bruto ${formatCurrency(result.grossAmount)}, obustava ${formatCurrency(result.totalWithheld)}, neto ${formatCurrency(result.amount)}`
      : `Isplata kreirana: ${formatCurrency(result.amount)}`;

  if (result.grossAmount !== result.requestedAmount) {
    return `${head} (zatraženo ${formatCurrency(result.requestedAmount)}, dio stavki je u međuvremenu već isplaćen)`;
  }
  return head;
}

type State = "pending" | "resolved";
type ConfirmAction = "approve" | "reject";

const RESOLUTION_LABEL: Record<string, string> = {
  approved: "Odobreno",
  rejected: "Odbijeno",
  withdrawn: "Povučeno",
};

export function PayoutRequestsSection() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>("pending");
  const [page, setPage] = useState(1);
  const [confirming, setConfirming] = useState<{ item: AdminPayoutRequest; action: ConfirmAction } | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  const { data, isFetching, isError } = useQuery({
    queryKey: ["admin", "payout-requests", state, page],
    queryFn: () =>
      apiGet<AdminPayoutRequestsResponse>(
        `/api/admin/payout-requests?state=${state}&page=${page}`,
        "Greška pri učitavanju zahtjeva",
      ),
  });

  const changeState = (v: State) => {
    setState(v);
    setPage(1);
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "payout-requests"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "payouts"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend<ApproveResult>(
        `/api/admin/payout-requests/${id}/approve`,
        "POST",
        {},
        "Greška pri odobravanju",
      ),
    onSuccess: (result) => {
      setConfirming(null);
      toast.success(approveToastMessage(result));
      refresh();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rejectMutation = useMutation({
    mutationFn: (input: { id: string; note: string }) =>
      apiSend(`/api/admin/payout-requests/${input.id}/reject`, "POST", { note: input.note }, "Greška pri odbijanju"),
    onSuccess: () => {
      setConfirming(null);
      setRejectNote("");
      toast.success("Zahtjev odbijen");
      refresh();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <section className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          Zahtjevi za isplatu{state === "pending" && (data?.total ?? 0) > 0 ? ` (${data!.total})` : ""}
        </h2>
        <SegmentedFilter
          ariaLabel="Zahtjevi za isplatu"
          options={[
            { value: "pending", label: "Otvoreni" },
            { value: "resolved", label: "Riješeni" },
          ]}
          value={state}
          onChange={(v) => changeState(v as State)}
        />
      </div>

      {isFetching && items.length === 0 && !isError ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          <CircleNotch className="inline size-4 animate-spin" />
        </p>
      ) : isError ? (
        <p className="py-6 text-center text-sm text-destructive">
          Zahtjeve nije moguće učitati. Pokušajte ponovo osvježiti stranicu.
        </p>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {state === "pending" ? "Nema otvorenih zahtjeva." : "Nema riješenih zahtjeva."}
        </p>
      ) : (
        <div>
          {items.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center gap-4 border-b border-border/60 py-3 last:border-b-0">
              <span className="w-24 shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatDateShort(item.requestedAt)}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={item.note ?? undefined}>
                {item.therapistName}
              </span>
              <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{item.sessionCount} stavki</span>
              <span
                className="w-28 shrink-0 text-right text-sm font-semibold tabular-nums text-foreground"
                title={requestAmountTitle(item)}
              >
                {formatCurrency(item.paidAmount ?? item.amount)}
              </span>
              {state === "pending" ? (
                <div className="flex shrink-0 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirming({ item, action: "approve" })}
                  >
                    Odobri
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirming({ item, action: "reject" })}>
                    Odbij
                  </Button>
                </div>
              ) : (
                <span className="shrink-0 text-xs text-muted-foreground" title={item.adminNote ?? undefined}>
                  {item.resolution ? RESOLUTION_LABEL[item.resolution] : ""}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {!isError && items.length > 0 && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-border/60 pt-3">
          <span className="text-xs text-muted-foreground">
            Stranica {data!.page} od {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prethodna
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Sljedeća
            </Button>
          </div>
        </div>
      )}

      {confirming?.action === "approve" && (
        <div className="mt-4 rounded-xl border border-border/60 p-4">
          <p className="mb-2 text-sm font-medium text-foreground">
            Odobravanje zahtjeva: {confirming.item.therapistName}
          </p>
          <p className="mb-3 text-sm text-muted-foreground">
            {approvePreviewText(confirming.item, data?.withholdingRates ?? null)}
            Ova akcija je nepovratna.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate(confirming.item.id)}
            >
              {approveMutation.isPending && <CircleNotch className="size-4 animate-spin" />}
              Potvrdi odobravanje
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Odustani
            </Button>
          </div>
        </div>
      )}

      {confirming?.action === "reject" && (
        <div className="mt-4 rounded-xl border border-border/60 p-4">
          <p className="mb-2 text-sm font-medium text-foreground">
            Odbijanje zahtjeva: {confirming.item.therapistName}
          </p>
          <Textarea
            value={rejectNote}
            maxLength={500}
            placeholder="Obrazloženje za terapeuta"
            onChange={(e) => setRejectNote(e.target.value)}
          />
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              disabled={rejectNote.trim().length < 3 || rejectMutation.isPending}
              onClick={() => rejectMutation.mutate({ id: confirming.item.id, note: rejectNote.trim() })}
            >
              Potvrdi odbijanje
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setConfirming(null); setRejectNote(""); }}>
              Odustani
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
